import React from 'react'
import {Emit_main} from "../navigation"
import * as P from "../program-state"
import * as C from "../controls"
import {faExternalLink, faPlus} from '@fortawesome/free-solid-svg-icons'
import {useForm, Controller, SubmitHandler} from "react-hook-form"
import {z} from "zod"
import { useNavigate } from 'react-router-dom'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

type Custom_audience_input = {
    name: string
    brand_id: number
    media: any
}


export const Create_custom_audience_form = () => {
    const nav = useNavigate()
    P.run_once_on_component_creation(() => void P.poll_connected_brands(ps, cs))
    const [brands, ps, cs] = P.use_brands()
    const { register, handleSubmit, watch, control, formState: { errors } } = useForm<Custom_audience_input>()
    const onSubmit: SubmitHandler<Custom_audience_input> = async (data) => {
        const formData = new FormData();
        formData.append('name', data.name);
        formData.append('brand_id', data.brand_id.toString());
        formData.append('fileb', data.media[0]);

        const [ok, resp] = await P.issue_formdata_post(P.v1api`audiencesv2/custom-audiences`, formData, z.any(), cs.token)
        if (ok) {
            nav("/custom-audiences/")
        } else {
            console.log("Failed", resp)
        }
    }

    const brands_data = !!brands ? brands.map((b) => {
        return {key: String(b.id), display: b.name}
    }) : []

    return (
        <>
            <form onSubmit={handleSubmit(onSubmit)}>
            <C.Eres_bare_card className="tw-mb-6">
                <div className='tw-flex tw-items-center tw-justify-between'>
                    <div>
                    <C.Eres_header2xl className="tw-font-semibold">Create a new custom audience</C.Eres_header2xl>
                    <p>A custom audience is an ad targeting option that lets you find your existing audiences among people across social networks.</p>
                    </div>
                    <div className='tw-flex-shrink-0'>
                        <C.Eres_iconbutton id="f-save" icon={faPlus} type="submit">Add Custom Audience</C.Eres_iconbutton>
                    </div>
                </div>
            </C.Eres_bare_card>
            <C.Eres_hline/>

            <div className="tw-mt-3 tw-grid tw-grid-cols-1 lg:tw-grid-cols-[55%,1fr] tw-gap-3">

            <C.Eres_card className="tw-h-fit">
                <div className="tw-p-3">
                        <div className="tw-grid tw-grid-cols-[auto,1fr] tw-gap-3 tw-items-center">
                            <C.Eres_form_label htmlFor="eres-audience-name" required>Audience name</C.Eres_form_label>
                            <C.Eres_line_edit id="eres-audience-name" aria-label="name" defaultValue="" type="text" extra={register("name")} />
                            <C.Eres_form_label htmlFor="eres-brand" required>Brand</C.Eres_form_label>
                            <Controller name="brand_id" control={control} render={({field}) => (
                                <C.Eres_combobox
                                    id="1"
                                    data={brands_data}
                                    defaultValue={brands_data.length ? brands_data[0].key : undefined}
                                    search_placeholder="Search for a brand ..."
                                    empty_selection_text="No brand selected"
                                    unselect_placeholder="Don't use a brand"
                                    no_matches_placeholder="No matching brands"
                                    {...field}
                                />
                            )}/>
                            <C.Eres_form_label htmlFor="eres-audience-media" required>CSV File</C.Eres_form_label>
                            <div>
                                <input className={`tw-block tw-w-full tw-text-gray-900 tw-border tw-cursor-pointer tw-bg-gray-50 tw-focus:outline-none ${C.form_element_styles}`} aria-describedby="user_avatar_help" id="user_avatar" type="file" {...register("media")} />
                                <div className="tw-mt-1 tw-text-sm tw-text-gray-500 tw-dark:text-gray-300" id="user_avatar_help">A media can be an image or video to be used as your media for ad</div>
                            </div>
                        </div>
                </div>
            </C.Eres_card>


            <C.Eres_bare_card>
                    <C.Eres_header2xl className="tw-font-semibold tw-my-2">Preparing Your Custom List</C.Eres_header2xl>
                    <div className='tw-space-y-0.5'>
                        <p>The maximum file upload size is 20MB. Include only one contact per row.
                        We have a <C.Eres_inline_link href="">CSV example</C.Eres_inline_link>.
                        We suggest you include as many contacts as possible. For best results, each row should include at least an email address or telephone number.</p>
                        <p>Contact lists may not contain or reflect health information or sensitive personal data. By uploading a list, you certify the uploaded list does not contain or reflect such information.</p>
                    </div>
            </C.Eres_bare_card>
            <C.Eres_bare_card>
                    <C.Eres_header2xl className="tw-font-semibold tw-my-2">Custom Audience Terms of Service</C.Eres_header2xl>
                    <div className='tw-space-y-0.5'>
                        <p>Each ad network supported by Eres has Terms of Service you must agree to to use custom audiences on their network. When submitting a custom audience to us, you're indicating that you agree to the terms of service for every network you've linked to Eres.</p>
                        <p className="tw-pt-3">Review each network's terms:
                        <ul className="tw-list-disc tw-list-inside">
                        <li>
                            <C.Eres_inline_link href="https://business.facebook.com/ads/manage/customaudiences/tos/" target="_blank" rel="noopener noreferrer">
                                Facebook Customer List Custom Audiences Terms <FontAwesomeIcon icon={faExternalLink} className="tw-h-3 tw-w-3"/>
                            </C.Eres_inline_link>
                        </li>
                        <li>
                            <C.Eres_inline_link href="https://www.linkedin.com/legal/sas-terms" target="_blank" rel="noopener noreferrer">
                                LinkedIn Ads Agreement <FontAwesomeIcon icon={faExternalLink} className="tw-h-3 tw-w-3"/>
                            </C.Eres_inline_link>
                        </li>
                        <li>
                            <C.Eres_inline_link href="https://ads.tiktok.com/i18n/official/policy/custom-audience-terms" target="_blank" rel="noopener noreferrer">
                                TikTok Custom Audiences (Customer File) Terms <FontAwesomeIcon icon={faExternalLink} className="tw-h-3 tw-w-3"/>
                            </C.Eres_inline_link>
                        </li>
                        </ul>
                        </p>
                    </div>
            </C.Eres_bare_card>
            </div>
            </form>
        </>
    )
}

const Create_custom_audience: React.FC = () => {
    return (
        <Emit_main page_id="PAGE-custom-audiences">
            <Create_custom_audience_form />
        </Emit_main>
    )
};

export default Create_custom_audience;
